import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { pluck } from '/imports/utils';
import { Doc, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateBoolean, validateString } from '/imports/validators';

// Defined HERE (client-safe) rather than in the server integration: these are imported by UI code, and a
// runtime import from /server/ drags server-only deps (jsonwebtoken, extract-zip, mime-types → node 'path'
// and 'stream') into the client bundle and breaks the build. Types alone would be elided; values are not.
/** Which FOMware API generation an account is on. Stored per credential. */
export const fomwareApiVersions = ['v2', 'v1'] as const;
export type FomwareApiVersion = typeof fomwareApiVersions[number];

/** Normalized site shape shared by both API generations (v1 field names are mapped onto it). */
export interface FomwareSite {
  siteId: string;
  siteName: string;
  siteAddress?: string;
  siteType?: number;          // enums DIFFER by version — v2: 0 household/1 commercial/2 other;
                              // v1: 1 residential PV/2 commercial PV/3 utility/4 other/5 ESS
  site4QType?: number;        // v2 only — 0: photovoltaic, 1: mix
  siteStatus?: number;        // enums DIFFER — v2: 2 run/3 offline/4 abnormal;
                              // v1: 1 building/2 running/3 offline/4 abnormal/5 abandoned/6 alert
  installedCapacity?: string; // v2 reports kWp as a string; v1 reports kW as a number (stringified)
  tzID?: string;              // v2: IANA. v1: may be "GMT+8" style — normalize with fomwareZone()
  tzOffsetHours?: number;     // v1 only, used as the tz fallback
  createdAt?: number;
  updatedAt?: number;
}

// FOMware's createdAt/updatedAt are epoch millis and collide with Doc's Date fields, so they are stored
// under site-prefixed names and the Doc timestamps keep their usual meaning (when WE created the row).
export interface FomwareMeter extends Doc, Omit<FomwareSite, 'createdAt' | 'updatedAt'> {
  _id: string;
  meterId: string;        // → ProductionMeters._id
  credentialId: string;
  siteCreatedAt?: number;
  siteUpdatedAt?: number;
}
export type Model = FomwareMeter;

export const FomwareMeters = new Mongo.Collection<FomwareMeter>('fomwareMeters');

type Refs = References<Model>

export const modelClass: ModelClass<Model, Refs> = {
  collection: FomwareMeters,
  editableFields: ['active'],
  listMethod: DeclareMethod<ListMethodArg<FomwareMeter>, ListMethodResponse<FomwareMeter, Refs>>('listFomwareMeters'),
  getMethod: DeclareMethod<string, GetMethodResponse<FomwareMeter, Refs>>('getFomwareMeter'),
  createMethod: DeclareMethod<Partial<FomwareMeter>, string>('createFomwareMeter'),
  updateMethod: DeclareMethod<Partial<FomwareMeter>, string>('updateFomwareMeter'),
  deleteMethod: DeclareMethod<string>('deleteFomwareMeter'),
  pathPrefix: '/fomware-meters',
  asString: (document) => document.siteName || document._id,
  modelName: 'Fomware',
  pluralName: 'Fomwares',
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { siteName: new RegExp(search, 'gi') },
        { siteId: new RegExp(search, 'gi') },
      ]
    };
  },
  validate: async (document: Partial<FomwareMeter>) => {
    const errors: { [key: string]: string[] } = {};
    validateBoolean(errors, document, 'active');
    validateString(errors, document, 'meterId', { required: true });
    return errors;
  },
};

export const pullFomwareMeterData = DeclareMethod<{ _id: string, days?: number }>('pullFomwareMeterData');

Meteor.isServer && Meteor.startup(async () => {
  await FomwareMeters.createIndexAsync({ meterId: 1 });
  await FomwareMeters.createIndexAsync({ siteId: 1 });
});

// --- Credentials. Stored in Mongo (not settings.json) so multiple FOMware accounts can coexist and be
//     managed at runtime. `baseUrl` is per-credential because FOMware's base URL is not a fixed value —
//     it differs per platform. `token` is encrypted at rest and never published to the client. ---
export interface FomwareCredential extends Doc {
  _id: string;
  orgId: string;
  label?: string;      // which FOMware account/platform this is, for the admin list
  baseUrl: string;
  apiVersion?: FomwareApiVersion;  // which FOMware API generation this account is on (defaults to v2)
  token: string;       // encrypted
  expiresAt?: Date;    // FOMware tokens expire; surfaced so a dead token is obvious before data stops
}

export const FomwareCredentials = new Mongo.Collection<FomwareCredential>('fomwareCredentials');

export const createFomwareCredential = DeclareMethod<Partial<FomwareCredential>, string>('createFomwareCredential');
export const updateFomwareCredential = DeclareMethod<Partial<FomwareCredential>>('updateFomwareCredential');
export const getFomwareCredentialsForOrg = DeclareMethod<string, FomwareCredential[]>('getFomwareCredentialsForOrg');
export const pullFomwareSites = DeclareMethod<string, { numSystems: number; numCreated: number; }>('pullFomwareSites');

/** Connectivity check for a base url + raw token, before saving it as a credential. */
export const probeFomwareCredential = DeclareMethod<{ baseUrl?: string, apiVersion?: FomwareApiVersion, token: string }, {
  baseUrl: string; ok: boolean; apiVersion: string; sites: number; message?: string; code?: string;
  sample?: {
    siteId: string; siteName: string; tzID?: string; zone?: string; installedCapacity?: string;
    intervals: number; nonZeroIntervals: number; firstStart?: Date; lastStart?: Date;
    firstStartLocal?: string; lastStartLocal?: string;
    totalKwh: number; toGridIntervals?: number; toGridKwh?: number;
  };
}>('probeFomwareCredential');

/**
 * Validates a FOMware credential's required fields.
 * @param credential - credential to validate
 * @returns map of field name to error messages
 */
export const validateFomwareCredential = (credential: Partial<FomwareCredential>) => {
  const errors: { [key: string]: string[] } = {};
  validateString(errors, credential, 'orgId', { required: true });
  validateString(errors, credential, 'baseUrl', { required: true });
  validateString(errors, credential, 'token', { required: true });
  return errors;
};

/**
 * Picks the persistable fields from a FOMware credential.
 * @param credentials - raw credential input
 * @returns credential with only the allowed fields
 */
export const cleanFomwareCredentials = (credentials: Partial<FomwareCredential>) =>
  pluck(credentials, ['orgId', 'label', 'baseUrl', 'apiVersion', 'token', 'expiresAt']) as FomwareCredential;
